import { useEffect, useState, type ReactNode } from 'react';
import { Play } from 'lucide-react';
import { KanameMark, type MarkCue } from '@/components/brand/KanameMark';
import { fanOf, petalsFor } from '@/components/brand/petals';
import { Button, Card, cn } from '@/components/ui';
import { animations, durations, easings } from '../tokens';
import { CourierScene, ObserverTile } from './CourierScene';
import { Code, Section, SubTitle } from './Section';

/* ---------- Знак: ожидание и ошибки (R42) — у каждой анимации «проиграть» ---------- */

/** Плитка анимации знака: тёмная сцена (лепестки светлые), подпись-код, пояснение, «проиграть». */
function MarkTile({
  code,
  note,
  onPlay,
  children,
}: {
  code: string;
  note: string;
  onPlay: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-line bg-card p-4">
      <div className="grid h-32 w-full place-items-center rounded-md bg-night">{children}</div>
      <Code>{code}</Code>
      <span className="text-center text-2xs text-fg-muted">{note}</span>
      <Button size="sm" variant="ghost" leftIcon={<Play />} onClick={onPlay}>
        проиграть
      </Button>
    </div>
  );
}

/** Шаг образца — как «Медленно» в «Основе». */
const REVEAL_STEP_MS = 600;

/** Знак-загрузчик: шаги по 600 мс (reveal="step"), шестой лепесток — досадкой на «готово» (reveal="snap"). */
function RevealSample({ run }: { run: number }) {
  const [done, setDone] = useState(6);
  // Новый прогон — с нуля; шаги — таймером.
  const [seen, setSeen] = useState(run);
  if (seen !== run) {
    setSeen(run);
    setDone(0);
  }
  useEffect(() => {
    if (done >= 6) return undefined;
    const t = window.setTimeout(() => setDone((d) => d + 1), REVEAL_STEP_MS);
    return () => window.clearTimeout(t);
  }, [done]);
  return (
    <KanameMark petals={fanOf(petalsFor(done, 6))} reveal={done >= 6 ? 'snap' : 'step'} className="size-20" />
  );
}

function MarkMotion() {
  const [runs, setRuns] = useState<Record<string, number>>({});
  const play = (name: string) => setRuns((r) => ({ ...r, [name]: (r[name] ?? 0) + 1 }));
  const n = (name: string) => runs[name] ?? 0;
  const cue = (name: string, gesture: MarkCue['gesture']): MarkCue | null =>
    n(name) > 0 ? { gesture, at: n(name) } : null;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <MarkTile
        code="animate-alarm-ring"
        note="два удара кольцом цвета сбоя, 900 мс; меньше трёх вспышек в секунду"
        onPlay={() => play('ring')}
      >
        <span
          key={n('ring')}
          aria-hidden
          className={cn('block h-9 w-32 rounded-md bg-brand', n('ring') > 0 && 'animate-alarm-ring')}
        />
      </MarkTile>
      <MarkTile
        code="animate-core-breathe-quick"
        note="ядро при сбое дышит вдвое чаще — 1,8 с против 3,6 с, в такт общим часам"
        onPlay={() => play('quick')}
      >
        <span key={n('quick')} className="flex items-center gap-8">
          <KanameMark open={false} breathing className="size-16" />
          <KanameMark open={false} rhythm="quick" className="size-16" />
        </span>
      </MarkTile>
      <MarkTile
        code="cue alarm · ALARM_MS 900"
        note="удар ядра: два удара и красное кольцо; повтор чаще раза в 1,5 с схлопывается"
        onPlay={() => play('alarm')}
      >
        <KanameMark breathing cue={cue('alarm', 'alarm')} className="size-20" />
      </MarkTile>
      <MarkTile
        code="cue burst · observer-burst"
        note="кольцо в тоне ядра расходится и гаснет, 700 мс"
        onPlay={() => play('burst')}
      >
        <KanameMark breathing cue={cue('burst', 'burst')} className="size-20" />
      </MarkTile>
      <MarkTile
        code={'reveal="step" → "snap"'}
        note="лепесток на пройденный шаг за 640 мс; шестой — досадкой за 160 мс"
        onPlay={() => play('reveal')}
      >
        <RevealSample run={n('reveal')} />
      </MarkTile>
    </div>
  );
}

export function MotionSection() {
  // Перезапуск анимации — через смену key (перемонтирование образца).
  const [runs, setRuns] = useState<Record<string, number>>({});
  const play = (name: string) => setRuns((r) => ({ ...r, [name]: (r[name] ?? 0) + 1 }));

  return (
    <Section
      id="motion"
      title="Motion"
      lead="Четыре длительности и именованные кривые; рецепты вместо наборов классов (R13). Tailwind v4 анимирует отдельные transform-свойства, поэтому transition-interactive перечисляет их явно. prefers-reduced-motion уважается глобально (R19)."
    >
      <div className="grid gap-6 md:grid-cols-2">
        <div className="flex flex-col divide-y divide-line rounded-lg border border-line bg-card">
          {durations.map((d) => (
            <div
              key={d.utility}
              className="grid grid-cols-[9rem_4rem_1fr] items-center gap-3 px-5 py-3 text-xs"
            >
              <Code>{d.utility}</Code>
              <span className="font-mono text-fg-subtle">{d.ms} ms</span>
              <span className="text-fg-muted">{d.usage}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col divide-y divide-line rounded-lg border border-line bg-card">
          {easings.map((e) => (
            <div key={e.utility} className="grid grid-cols-[9rem_1fr] items-center gap-3 px-5 py-3 text-xs">
              <Code>{e.utility}</Code>
              <span className="font-mono text-fg-subtle">cubic-bezier({e.curve})</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <SubTitle>Анимации</SubTitle>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {animations.map((a) => (
            <div
              key={a}
              className="flex flex-col items-center gap-3 rounded-lg border border-line bg-card p-4"
            >
              <div className="grid h-16 place-items-center">
                <div
                  key={runs[a] ?? 0}
                  className={cn(
                    'size-10 rounded-md bg-gradient-to-br from-brand-hover to-brand-active shadow-brand',
                    (runs[a] ?? 0) > 0 && a,
                  )}
                  aria-hidden
                />
              </div>
              <Code>{a}</Code>
              <Button size="sm" variant="ghost" leftIcon={<Play />} onClick={() => play(a)}>
                проиграть
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <SubTitle>Знак: ожидание и ошибки (R42)</SubTitle>
        <MarkMotion />
      </div>

      <div className="flex flex-col gap-3">
        <SubTitle>Полёт ядра к событию (помощник, R36)</SubTitle>
        <div className="flex flex-col gap-3 rounded-lg border border-line bg-card p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="type-subheading text-fg">CoreCourier — ядро-вестник</span>
            <Code>
              ObserverProvider home="core" · body · EVENT_PLAN · data-core-home · data-observer-block
            </Code>
          </div>
          <CourierScene />
        </div>
        <ObserverTile />
      </div>

      <div className="flex flex-col gap-3">
        <SubTitle>Рецепты</SubTitle>
        <div className="grid gap-4 md:grid-cols-2">
          <Card interactive className="p-5">
            <div className="type-subheading text-fg">lift</div>
            <p className="mt-1 text-xs text-fg-muted">
              Наведи: масштаб 1.015 от центра, тень lift, рамка strong — 480 ms ease-out-expo. Без translate,
              чтобы не дрожало у края курсора.
            </p>
          </Card>
          <div className="flex flex-col gap-3 rounded-lg border border-line bg-card p-5">
            <div className="type-subheading text-fg">transition-interactive</div>
            <p className="text-xs text-fg-muted">
              Цвета и тень 200 ms ease-out, scale/translate 240 ms bouncy — все кнопки и поля.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button>Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="ghost">Ghost</Button>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
